import { getGatedIdacBackendUrl } from '../utilities/env';
import {
  Action,
  ThunkAction,
  CsrfToken,
  MicrobrandingParams,
  ChangePasswordFieldErrors,
  ChangePasswordError,
  UiError,
} from '../types';
import queryString from 'query-string';
import { updateCsrfToken } from './csrf-token-actions';
import messages from '../containers/ChangePasswordPage.messages';
import { IntlShape } from 'react-intl';
import { AnalyticsClient } from '../utilities/analytics/analytics-web-client';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';

export type ChangePasswordAction =
  | { type: 'CHANGE_PASSWORD_REQUEST_INITIATE' }
  | { type: 'CHANGE_PASSWORD_REQUEST_SUCCESS' }
  | { type: 'CHANGE_PASSWORD_REQUEST_FAILED'; error: ChangePasswordError }
  | { type: 'CHANGE_PASSWORD_REQUEST_FIELD_ERRORS'; fieldErrors: ChangePasswordFieldErrors };

export type ChangePasswordType = 'security-password-reset' | 'password-reset';

export const changePasswordInitiate = (): Action => ({
  type: 'CHANGE_PASSWORD_REQUEST_INITIATE',
});

export const changePasswordSuccess = (): Action => ({
  type: 'CHANGE_PASSWORD_REQUEST_SUCCESS',
});

export const changePasswordFailure = (error: ChangePasswordError): Action => ({
  type: 'CHANGE_PASSWORD_REQUEST_FAILED',
  error,
});

export const changePasswordFieldErrors = (fieldErrors: ChangePasswordFieldErrors): Action => ({
  type: 'CHANGE_PASSWORD_REQUEST_FIELD_ERRORS',
  fieldErrors,
});

export interface ChangePasswordRequest {
  signature: string;
  password: string;
  resetType: ChangePasswordType;
}

// redirectTo is our discriminant
export type ChangePasswordResponse = ChangePasswordSuccess | ChangePasswordFailure;

export interface ChangePasswordSuccess {
  redirectTo: string;
}

export interface ChangePasswordFailure {
  redirectTo: undefined;
  uiErrors: UiError[];
  formErrors: string[];
  fieldErrors: ChangePasswordFieldErrors;
  csrfToken?: CsrfToken;
}

export type PasswordChangeRequestParams = {
  signature: string;
  password: string;
  resetType: ChangePasswordType;
  csrfToken: CsrfToken;
  microbranding?: MicrobrandingParams;
  intl: IntlShape;
  analyticsClient: AnalyticsClient;
};

export const requestChangePassword =
  (requestParams: PasswordChangeRequestParams, navigate: (url: string) => void): ThunkAction =>
  dispatch => {
    const changePasswordAnalyticsError = {
      passwordCaseError: 'passwordResetDoesntMeetPolicy',
      passwordDigitOrSpecialError: 'passwordResetDoesntMeetPolicy',
      passwordLengthError: 'passwordResetDoesntMeetPolicy',
      passwordUsedBefore: 'passwordResetPreviouslyUsed',
      passwordLeaked: 'passwordLeaked',
      passwordCommon: 'passwordCommon',
      invalidPassword: 'invalidPassword',
      unexpectedError: 'passwordResetUnexpectedError',
      passwordStrengthErrorWeak: 'passwordResetDoesntMeetPolicy',
      passwordStrengthErrorFair: 'passwordResetDoesntMeetPolicy',
      passwordStrengthErrorGood: 'passwordResetDoesntMeetPolicy',
      passwordStrengthErrorStrong: 'passwordResetDoesntMeetPolicy',
      passwordStrengthErrorVeryStrong: 'passwordResetDoesntMeetPolicy',
    };
    const { signature, password, resetType, csrfToken, microbranding, intl, analyticsClient } =
      requestParams;

    const qs =
      microbranding && Object.keys(microbranding).length
        ? `?${queryString.stringify(microbranding)}`
        : '';

    dispatch(changePasswordInitiate());

    if (!password) {
      const message = intl.formatMessage(messages.emptyPasswordErrorMessage);
      dispatch(
        changePasswordFieldErrors({
          password: message,
        })
      );
      return Promise.resolve();
    }

    const request: ChangePasswordRequest = {
      password,
      resetType,
      signature,
    };

    const path = getGatedIdacBackendUrl(`/rest/changepassword${qs}`);

    return fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(request),
    })
      .then(response => response.json())
      .then((response: ChangePasswordResponse) => {
        if (response.redirectTo !== undefined) {
          dispatch(changePasswordSuccess());
          navigate(response.redirectTo);
        } else {
          const { csrfToken, uiErrors, fieldErrors } = response;

          // SIS doesn't set a new CSRF token on every request (like aid-signup does), and so it
          // doesn't return one. We need to handle both cases though.
          if (csrfToken) {
            dispatch(updateCsrfToken(csrfToken));
          }

          if (uiErrors && uiErrors.length) {
            const subjectId =
              changePasswordAnalyticsError[uiErrors[0].key] || 'passwordResetUnknownUiError';
            analyticsClient.errorShownEvent('accountRecoveryScreen', subjectId);

            dispatch(changePasswordFailure(<ChangePasswordError>uiErrors[0].key));
          }

          if (Object.entries(fieldErrors).length) {
            dispatch(changePasswordFieldErrors(fieldErrors));
          }
        }
      })
      .catch(error => {
        dispatch(changePasswordFailure('unknown'));
        captureErrorMessage('Change password failed', error);
      });
  };
